import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const VALUES = [
  {
    k: "Craft",
    v: "We ship interfaces we’d be proud to use. No half-work, no lorem ipsum in production.",
  },
  {
    k: "Ownership",
    v: "We run our own products. That discipline shows up in every client engagement.",
  },
  {
    k: "Rooted",
    v: "Hyderabad-based studio serving clients globally. Tech grounded in real operational discipline.",
  },
  {
    k: "Calm",
    v: "No theatrics, no hero culture. Boring reliable systems and honest updates.",
  },
];

const TIMELINE = [
  { y: "2024 · Q1", t: "Formed as an LLP in Hyderabad under founder-director Obul Satish Sagalamarri." },
  { y: "2024 · Q3", t: "Broke ground on our two consumer brands — LiveCuts and Flockfolio." },
  { y: "2025 · Q1", t: "LiveCuts MVP live in Hyderabad; first cold-chain deliveries." },
  { y: "2025 · Q3", t: "Flockfolio onboards its first 40 investors via passiveiearn.com." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-bone-300">
        <div className="container-x py-20 sm:py-28">
          <div className="overline">/ About</div>
          <h1
            data-testid="about-heading"
            className="mt-6 font-display font-black text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.95] tracking-tighter text-ink-olive max-w-4xl"
          >
            A small studio doing a
            <span className="font-serif italic font-light"> big-scope </span>
            of work.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ink-soft leading-relaxed">
            Thiruhanarayano Innovations LLP is an independent studio that
            owns and operates two consumer-facing brands — LiveCuts and
            Flockfolio. We combine deep engineering with slow, patient
            product work.
          </p>
        </div>
      </section>

      <section className="border-b border-bone-300 bg-white">
        <div className="container-x py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">/ Mission</div>
            <h2 className="mt-4 font-serif italic font-light text-3xl sm:text-4xl text-ink-olive tracking-tight">
              Build things that outlast the launch.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-bone-300 border border-bone-300">
            {VALUES.map((v, i) => (
              <div
                key={v.k}
                className="bg-white p-8"
                data-testid={`about-value-${i}`}
              >
                <div className="mono-label text-terracotta">/ 0{i + 1}</div>
                <div className="mt-4 font-display font-bold text-2xl text-ink-olive">
                  {v.k}
                </div>
                <p className="mt-2 text-ink-soft leading-relaxed">{v.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-bone-300 bg-white">
        <div className="container-x py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <div className="overline">/ Leadership</div>
            <h2 className="mt-4 font-serif italic font-light text-3xl sm:text-4xl text-navy tracking-tight">
              Steered by a founder-director.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <div
              className="border border-bone-300 p-8 sm:p-10 bg-bone-50"
              data-testid="about-director-card"
            >
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-navy text-gold-soft font-display font-black text-2xl sm:text-3xl inline-flex items-center justify-center shrink-0">
                  OS
                </div>
                <div>
                  <div className="mono-label text-gold-deep">/ Director</div>
                  <div className="mt-2 font-display font-bold text-2xl sm:text-3xl text-navy">
                    Obul Satish Sagalamarri
                  </div>
                  <p className="mt-3 text-ink-soft max-w-xl leading-relaxed">
                    Founder-Director of Thiruhanarayano Innovations LLP.
                    Obul leads the studio out of Hyderabad, shepherding
                    the in-house LiveCuts and Flockfolio brands from
                    strategy to on-ground operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-bone-300">
        <div className="container-x py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline">/ Timeline</div>
            <h2 className="mt-4 font-serif italic font-light text-3xl sm:text-4xl text-ink-olive">
              A short, honest story.
            </h2>
            <p className="mt-4 text-ink-soft">
              We keep our pace slow on purpose. Below is what we’ve actually
              shipped — not what we plan to.
            </p>
          </div>
          <div className="lg:col-span-7 divide-y divide-bone-300 border-y border-bone-300">
            {TIMELINE.map((row, i) => (
              <div
                key={row.y}
                className="grid grid-cols-12 gap-4 py-6"
                data-testid={`timeline-row-${i}`}
              >
                <div className="col-span-4 sm:col-span-3 mono-label text-terracotta">
                  {row.y}
                </div>
                <div className="col-span-8 sm:col-span-9 font-display text-lg text-ink-olive">
                  {row.t}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink-olive text-bone">
        <div className="container-x py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <h3 className="lg:col-span-8 font-display font-black text-4xl sm:text-5xl tracking-tighter">
            Curious about a collaboration?
          </h3>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 bg-bone text-ink-olive px-6 py-3 font-sans font-medium hover:bg-terracotta hover:text-bone transition-colors"
            >
              Say hello <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
